import { ReactNode, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { copilotBaseUrl } from './api/baseUrl'
import { createSession, postConfirm, postInterrupt, postMessage, uploadDocuments } from './api/copilotApi'
import { closeStream, isStreamOpen, openStream } from './api/sse'
import { useRunGuard } from './hooks/useRunGuard'
import { CopilotApi, CopilotContext } from './state/CopilotContext'
import { initialState, reducer } from './state/machine'
import { clientId as makeClientId } from './util/id'

/** Safe subset of a failed request for logging: message + HTTP status only — never the raw error
 *  object. See the comment at its first call site (in `startRun`) for why. */
function safeErrorInfo(e: unknown): { message: string; status?: number } {
  const message = e instanceof Error ? e.message : String(e)
  const status = typeof e === 'object' && e !== null
    ? (e as { response?: { status?: unknown } }).response?.status
    : undefined
  return typeof status === 'number' ? { message, status } : { message }
}

/** Which message to show the operator for a failed copilot call. Branches on STATUS only: the proxy
 *  relays the service's body untouched but shapes its own errors differently, so there is no single
 *  body shape to read. */
function failureKey(e: unknown, genericKey: string): string {
  switch (safeErrorInfo(e).status) {
    case 401: return 'copilot.error.sessionExpired'
    case 403: return 'copilot.error.accessRevoked'
    case 413: return 'copilot.error.uploadRejected'
    default: return genericKey
  }
}

/** Maps the stream's codes onto the same messages the request path uses, so both paths tell the
 *  operator ONE story about the same rejection. */
function streamErrorKey(code: string): string {
  switch (code) {
    case 'auth': return 'copilot.error.sessionExpired'
    case 'forbidden': return 'copilot.error.accessRevoked'
    default: return 'copilot.error.streamLost'
  }
}

/** Upload attachments only when there are any; both startRun and answer accept optional files on
 *  an otherwise-identical path. */
async function uploadIfAny(baseUrl: string, files: File[]): Promise<{ documentIds: string[] }> {
  return files.length ? uploadDocuments(baseUrl, files) : { documentIds: [] }
}

export function CopilotProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [state, dispatch] = useReducer(reducer, initialState)
  const openClientIdsRef = useRef<Set<string>>(new Set())
  const sweepAbandonedRuns = useRunGuard(state.sessions)

  // Close ALL open streams on unmount, and stop the runs behind them. This is DEFENSIVE, not a live
  // path: every teardown reachable today navigates the document (both logout buttons end in
  // `window.location.replace`) and so fires `pagehide` instead, and `copilotEnabled` cannot change
  // without a reload because `useAuth`'s effect has `[]` deps. It earns its place by covering a
  // client-side unmount if one ever becomes reachable — `CopilotMount` already sits behind
  // `{user && customer && !isLoading}` in App.tsx, so a change to that condition would otherwise
  // leak live runs silently. `sweepAbandonedRuns` is deliberately stable, so this effect never
  // re-runs and the sweep cannot fire on a session that is still going.
  useEffect(() => {
    const ids = openClientIdsRef.current
    return () => {
      ids.forEach(closeStream)
      sweepAbandonedRuns()
    }
  }, [sweepAbandonedRuns])

  // Open the SSE stream for one turn. The server closes the stream after each terminal
  // frame (session_complete/error), so we MUST deregister here — otherwise isStreamOpen
  // stays true and the NEXT follow-up skips reopening, leaving that turn with no listener
  // (the "no info for the third question" bug). Shared by startRun and answer so both
  // paths behave identically.
  const openSessionStream = useCallback((cid: string, sessionId: string, baseUrl: string) => {
    openClientIdsRef.current.add(cid)
    console.debug('[copilot sse] opening stream', { clientId: cid, sessionId })
    const deregister = () => { closeStream(cid); openClientIdsRef.current.delete(cid) }
    openStream({
      baseUrl,
      clientId: cid,
      sessionId,
      onEvent: (event) => {
        dispatch({ type: 'SSE', clientId: cid, event, at: Date.now() })
        if (event.event === 'session_complete' || event.event === 'error') {
          console.debug('[copilot sse] terminal frame — deregistering stream', { clientId: cid, event: event.event })
          deregister()
        }
      },
      // Deregister on a connection/auth error too (not just on a terminal frame): otherwise a dropped
      // or 401'd stream leaves a stale-open entry, isStreamOpen(cid) stays true, and the NEXT follow-up
      // skips reopening — the same "no listener" hang as the terminal-frame case, on the error path.
      // `sse.ts` reports a CODE, not wording. Translating here rather than in the transport keeps UI
      // strings out of the transport module, and stops a raw '401' or an untranslated English phrase
      // reaching the timeline of a panel that is otherwise fully localised.
      onError: (code) => {
        console.debug('[copilot sse] stream error — deregistering stream', { clientId: cid, code })
        const message = t(streamErrorKey(code))
        dispatch({ type: 'SSE', clientId: cid, at: Date.now(), event: { event: 'error', data: { message } } })
        deregister()
      },
    })
  }, [t])

  const startRun = useCallback(async (files: File[], instruction: string) => {
    const cid = makeClientId()
    const baseUrl = copilotBaseUrl()
    dispatch({ type: 'NEW_SESSION', clientId: cid })
    dispatch({ type: 'UPLOAD_START', clientId: cid, title: (files[0]?.name ?? instruction.slice(0, 40)) || 'New session' })
    dispatch({
      type: 'USER_TURN', clientId: cid, at: Date.now(),
      text: instruction, files: files.map((f) => f.name),
    })
    try {
      const { documentIds } = await uploadIfAny(baseUrl, files)
      dispatch({ type: 'SESSION_CREATING', clientId: cid })
      // Create the session ONLY — passing instruction/documents here makes the backend auto-kick its
      // own turn, which together with the postMessage below would run the turn twice. The single
      // kickoff is postMessage.
      const { sessionId } = await createSession(baseUrl, {})
      dispatch({ type: 'SESSION_CREATED', clientId: cid, sessionId })
      openSessionStream(cid, sessionId, baseUrl)
      const { traceId } = await postMessage(baseUrl, sessionId, { text: instruction, documentIds })
      dispatch({ type: 'MESSAGE_TRACED', clientId: cid, traceId })
      dispatch({ type: 'MESSAGE_SENT', clientId: cid })
    } catch (e) {
      // Never log the raw error object here: an AxiosError carries `.config.headers.Authorization`
      // (the operator's live JWT) and `.config.data` (the typed instruction — party names, EORIs,
      // MRNs may be in it). The copilot backend is the untrusted party on this call and controls the
      // status/shape it rejects with, so this path is reachable on every turn from a misbehaving
      // service. Log a safe subset only — do not revert to logging `e` directly.
      console.error('[copilot] startRun failed', safeErrorInfo(e))
      dispatch({ type: 'SSE', clientId: cid, event: { event: 'error', data: { message: t(failureKey(e, 'copilot.error.startFailed')) } } })
    }
  }, [openSessionStream, t])

  const answer = useCallback(async (cid: string, text: string, files: File[] = []) => {
    const s = state.sessions[cid]
    // Record the operator's turn BEFORE the sessionId guard below, no matter what: otherwise a
    // follow-up typed into a session whose run never started (startRun's catch fired before
    // SESSION_CREATED, so sessionId is permanently null — that's the only place it's ever set)
    // vanishes with zero feedback, as if it was never sent.
    dispatch({ type: 'USER_TURN', clientId: cid, at: Date.now(), text, files: files.map((f) => f.name) })
    if (!s?.sessionId) {
      // This session slot cannot recover — there is no sessionId to retry against, and the
      // operator's only path forward is starting a new session. Say so instead of silently
      // dropping the message.
      dispatch({ type: 'SSE', clientId: cid, event: { event: 'error', data: { message: t('copilot.error.neverStarted') } } })
      return
    }
    const baseUrl = copilotBaseUrl()
    try {
      // Reopen for this turn if the previous turn's stream was closed by its terminal frame.
      if (!isStreamOpen(cid)) {
        openSessionStream(cid, s.sessionId, baseUrl)
      }
      // The service accepts documents on a follow-up message, so a mid-conversation attach
      // uploads first and passes the ids through with the text.
      const { documentIds } = await uploadIfAny(baseUrl, files)
      const { traceId } = await postMessage(baseUrl, s.sessionId, { text, documentIds })
      dispatch({ type: 'MESSAGE_TRACED', clientId: cid, traceId })
      dispatch({ type: 'MESSAGE_SENT', clientId: cid })
    } catch (e) {
      // Without this, a failed upload/postMessage (413, network drop) leaves the operator's turn
      // sitting in the thread with no run ever started and no error shown — the same failure mode
      // startRun's own try/catch exists to prevent. Log a safe subset only (see safeErrorInfo above
      // startRun) — do not revert to logging `e` directly.
      console.error('[copilot] answer failed', safeErrorInfo(e))
      dispatch({ type: 'SSE', clientId: cid, event: { event: 'error', data: { message: t(failureKey(e, 'copilot.error.sendFailed')) } } })
    }
  }, [state.sessions, openSessionStream, t])

  // Both of these are called bare from an onClick (ConfirmationCard, RunningStep), so a rejection here
  // is an unhandled promise with nothing shown to the operator: the button just appears to do nothing.
  // That used to be masked — a copilot 401 logged them out, which at least was visible — and the
  // interceptor deliberately no longer does that, so these two need the same error surface the other
  // two call sites have always had.
  const confirm = useCallback(async (cid: string, approved: boolean, reason?: string) => {
    const s = state.sessions[cid]
    if (!s?.sessionId || !s.pendingConfirmation) return
    const baseUrl = copilotBaseUrl()
    try {
      await postConfirm(baseUrl, s.sessionId, {
        confirmationId: s.pendingConfirmation.confirmationId, approved, reason,
      })
      dispatch({ type: 'CONFIRM_SENT', clientId: cid })
    } catch (e) {
      // Safe subset only, for the reason spelled out above startRun's catch.
      console.error('[copilot] confirm failed', safeErrorInfo(e))
      dispatch({ type: 'SSE', clientId: cid, event: { event: 'error', data: { message: t(failureKey(e, 'copilot.error.confirmFailed')) } } })
    }
  }, [state.sessions, t])

  const interrupt = useCallback(async (cid: string) => {
    const s = state.sessions[cid]
    if (!s?.sessionId) return
    const baseUrl = copilotBaseUrl()
    try {
      await postInterrupt(baseUrl, s.sessionId)
      // AFTER the server accepts it, not before. `interruptRequested` is read in exactly one place —
      // the reducer's `error` branch — where it relabels the terminal frame as "Run cancelled." and
      // DISCARDS the real message. Setting it up front therefore did nothing for the UI and turned a
      // failed interrupt into a run reported as cancelled when it is still going, swallowing the
      // reason it failed.
      dispatch({ type: 'INTERRUPT_REQUESTED', clientId: cid })
    } catch (e) {
      console.error('[copilot] interrupt failed', safeErrorInfo(e))
      dispatch({ type: 'SSE', clientId: cid, event: { event: 'error', data: { message: t(failureKey(e, 'copilot.error.interruptFailed')) } } })
    }
  }, [state.sessions, t])

  const value = useMemo<CopilotApi>(() => ({
    state,
    open: () => dispatch({ type: 'OPEN' }),
    close: () => dispatch({ type: 'CLOSE' }),
    setActive: (cid) => dispatch({ type: 'SET_ACTIVE', clientId: cid }),
    startRun, answer, confirm, interrupt,
  }), [state, startRun, answer, confirm, interrupt])

  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>
}

// re-export for the mount point
export { closeStream }
